import { useEffect, useRef, useState } from "react";
import api, { euro, price, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { FileSpreadsheet, UploadCloud, ArrowRight, CheckCircle2, XCircle, History, Trash2, X, RotateCcw, Download } from "lucide-react";

const inp = "w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40 transition-colors";

export default function ImportVendite() {
  const [step, setStep] = useState("upload"); // upload | map | review
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({ name: "", quantity: "", unit_price: "", vat: "" });
  const [match, setMatch] = useState(null);
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const fileRef = useRef();

  const loadSessions = () => api.get("/sales/import/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSessions(); api.get("/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);

  const reset = () => { setStep("upload"); setParsed(null); setMatch(null); setMapping({ name: "", quantity: "", unit_price: "", vat: "" }); };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/sales/import/parse", form, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data);
      setMapping({ name: data.guess?.name || "", quantity: data.guess?.quantity || "", unit_price: data.guess?.unit_price || "", vat: data.guess?.vat || "" });
      setStep("map");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runMatch = async () => {
    if (!mapping.name) { toast.error("Seleziona almeno la colonna del nome prodotto"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sales/import/match", { rows: parsed.rows, mapping });
      setMatch(data);
      setStep("review");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleApply = (i) => setMatch({ ...match, matched: match.matched.map((m, idx) => idx === i ? { ...m, apply: !m.apply } : m) });
  const setUnmatched = (i, patch) => setMatch({ ...match, unmatched: match.unmatched.map((u, idx) => idx === i ? { ...u, ...patch } : u) });

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sales/import/commit", {
        filename: parsed.filename, mapping, matched: match.matched, unmatched: match.unmatched,
      });
      toast.success(`Import completato — ${data.updated_count} prezzi aggiornati, ${data.created_count || 0} prodotti creati`);
      reset(); loadSessions();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openDetail = async (sid) => {
    try { const { data } = await api.get(`/sales/import/sessions/${sid}`); setDetail(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delSession = async () => {
    await api.delete(`/sales/import/sessions/${confirmDel.id}`);
    toast.success("Sessione eliminata"); setConfirmDel(null); loadSessions();
  };

  const applyCount = match?.matched.filter((m) => m.apply && m.unit_price != null).length || 0;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Vendite</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Import Excel Vendite</h1>
          <p className="text-sm text-stone-500 mt-2">Carica il file Excel, mappa le colonne e abbina i prodotti all'Anagrafica per nome. I prezzi di vendita vengono aggiornati solo dopo la tua conferma.</p>
        </div>
        {step !== "upload" && (
          <button data-testid="reset-import-btn" onClick={reset} className="flex items-center gap-2 rounded-full border border-stone-300 text-stone-600 px-4 py-2 text-sm font-semibold hover:bg-stone-100 transition-colors">
            <RotateCcw size={15} /> Ricomincia
          </button>
        )}
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-xs font-semibold">
        {["upload", "map", "review"].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-full ${step === s ? "bg-brand text-white" : "bg-stone-200 text-stone-500"}`}>
              {i + 1}. {s === "upload" ? "Carica" : s === "map" ? "Mappa colonne" : "Verifica & conferma"}
            </span>
            {i < 2 && <ArrowRight size={14} className="text-stone-300" />}
          </div>
        ))}
      </div>

      {step === "upload" && (
        <div data-testid="upload-zone" onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-stone-300 bg-stone-50 hover:bg-stone-100 rounded-sm p-12 text-center cursor-pointer transition-colors">
          <input ref={fileRef} type="file" accept=".xlsx" hidden data-testid="excel-input"
            onChange={(e) => upload(e.target.files[0])} />
          <UploadCloud size={40} strokeWidth={1.3} className="mx-auto text-brand mb-3" />
          <p className="font-serif text-2xl text-stone-900">{busy ? "Lettura file…" : "Carica il file Excel delle vendite"}</p>
          <p className="text-sm text-stone-500 mt-1">Formato .xlsx — la prima riga deve contenere le intestazioni delle colonne</p>
        </div>
      )}

      {step === "map" && parsed && (
        <div className="space-y-6">
          <div className="bg-white border border-stone-200 rounded-sm p-6">
            <div className="flex items-center gap-2 mb-4"><FileSpreadsheet size={18} className="text-brand" /><h3 className="font-serif text-2xl text-stone-900">Mappa le colonne</h3></div>
            <p className="text-sm text-stone-500 mb-4">{parsed.filename} — {parsed.total_rows} righe rilevate. Indica quale colonna del file corrisponde a ciascun campo.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[["name", "Nome prodotto *"], ["quantity", "Quantità venduta"], ["unit_price", "Prezzo unitario vendita"], ["vat", "IVA vendite (opz.)"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">{label}</label>
                  <select data-testid={`map-${k}`} value={mapping[k]} onChange={(e) => setMapping({ ...mapping, [k]: e.target.value })} className={`${inp} mt-1.5`}>
                    <option value="">— nessuna —</option>
                    {parsed.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
            <div className="px-6 py-3 border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500 font-bold">Anteprima (prime 5 righe)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-stone-200 text-stone-500">{parsed.columns.map((c) => <th key={c} className="text-left py-2 px-4 font-bold whitespace-nowrap">{c}</th>)}</tr></thead>
                <tbody>
                  {parsed.rows.slice(0, 5).map((r, i) => (
                    <tr key={i} className="border-b border-stone-100">{parsed.columns.map((c) => <td key={c} className="py-2 px-4 text-stone-600 whitespace-nowrap">{String(r[c] ?? "")}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex justify-end">
            <button data-testid="run-match-btn" onClick={runMatch} disabled={busy}
              className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors disabled:opacity-60">
              {busy ? "Analisi…" : "Analizza e abbina"} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === "review" && match && (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Righe totali" value={match.summary.total} />
            <StatCard label="Abbinati" value={match.summary.matched} tone="brand" icon={<CheckCircle2 size={16} />} />
            <StatCard label="Non abbinati" value={match.summary.unmatched} tone="alert" icon={<XCircle size={16} />} />
          </div>

          <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
            <div className="px-6 py-3 border-b border-stone-200 flex items-center justify-between">
              <h3 className="font-serif text-xl text-stone-900">Prodotti abbinati</h3>
              <span className="text-xs text-stone-500">{applyCount} prezzi verranno aggiornati</span>
            </div>
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-stone-50"><tr className="border-b border-stone-200 text-xs uppercase tracking-[0.1em] text-stone-500">
                  <th className="text-center py-3 px-4 font-bold">Aggiorna</th>
                  <th className="text-left py-3 px-4 font-bold">Prodotto (Excel → Anagrafica)</th>
                  <th className="text-right py-3 px-4 font-bold">Q.tà</th>
                  <th className="text-right py-3 px-4 font-bold">Prezzo attuale</th>
                  <th className="text-right py-3 px-4 font-bold">Nuovo prezzo</th>
                  <th className="text-right py-3 px-4 font-bold">Margine nuovo</th>
                </tr></thead>
                <tbody>
                  {match.matched.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-stone-400">Nessun prodotto abbinato.</td></tr>}
                  {match.matched.map((m, i) => {
                    const margin = m.unit_price != null && m.best_purchase != null ? m.unit_price - m.best_purchase : null;
                    const mpct = margin != null && m.unit_price ? (margin / m.unit_price * 100).toFixed(1) : null;
                    return (
                      <tr key={i} data-testid={`matched-row-${i}`} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-3 px-4 text-center">
                          <input data-testid={`apply-check-${i}`} type="checkbox" checked={!!m.apply} disabled={m.unit_price == null} onChange={() => toggleApply(i)} />
                        </td>
                        <td className="py-3 px-4"><span className="text-stone-500">{m.name}</span> <ArrowRight size={11} className="inline text-stone-300" /> <span className="font-semibold text-stone-800">{m.product_name}</span></td>
                        <td className="py-3 px-4 text-right text-stone-600">{m.quantity ?? "—"}</td>
                        <td className="py-3 px-4 text-right text-stone-500">{m.current_sale_price != null ? euro(m.current_sale_price) : "—"}</td>
                        <td className="py-3 px-4 text-right font-semibold text-stone-900">{m.unit_price != null ? euro(m.unit_price) : "—"}</td>
                        <td className="py-3 px-4 text-right">{mpct != null ? <span className={`text-xs font-bold px-2 py-1 rounded-full ${mpct < 20 ? "text-alert bg-alert/10" : "text-brand bg-brand/10"}`}>{mpct}%</span> : <span className="text-xs text-stone-400">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {match.unmatched.length > 0 && (
            <div data-testid="unmatched-block" className="bg-white border border-stone-200 rounded-sm overflow-hidden">
              <div className="px-6 py-3 border-b border-stone-200 flex items-center gap-2">
                <XCircle size={16} className="text-alert" /><h3 className="font-serif text-xl text-stone-900">Non abbinati ({match.unmatched.length})</h3>
                <span className="text-xs text-stone-500">Spunta "Crea" per aggiungerli all'Anagrafica nel reparto scelto.</span>
              </div>
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-stone-50"><tr className="border-b border-stone-200 text-xs uppercase tracking-[0.1em] text-stone-500">
                    <th className="text-left py-3 px-4 font-bold">Nome (Excel)</th><th className="text-right py-3 px-4 font-bold">Q.tà</th><th className="text-right py-3 px-4 font-bold">Prezzo</th>
                    <th className="text-center py-3 px-4 font-bold">Crea</th><th className="text-left py-3 px-4 font-bold">Reparto</th>
                  </tr></thead>
                  <tbody>
                    {match.unmatched.map((u, i) => (
                      <tr key={i} data-testid={`unmatched-row-${i}`} className="border-b border-stone-100">
                        <td className="py-2.5 px-4 text-stone-700">{u.name}</td>
                        <td className="py-2.5 px-4 text-right text-stone-600">{u.quantity ?? "—"}</td>
                        <td className="py-2.5 px-4 text-right text-stone-600">{u.unit_price != null ? euro(u.unit_price) : "—"}</td>
                        <td className="py-2.5 px-4 text-center"><input data-testid={`create-check-${i}`} type="checkbox" checked={!!u.create} onChange={(e) => setUnmatched(i, { create: e.target.checked, category: u.category || "Altro" })} /></td>
                        <td className="py-2.5 px-4"><select data-testid={`unmatched-cat-${i}`} value={u.category || "Altro"} disabled={!u.create} onChange={(e) => setUnmatched(i, { category: e.target.value })} className="border border-stone-300 bg-white px-2 py-1 text-xs rounded-sm outline-none focus:ring-2 focus:ring-brand/40 disabled:bg-stone-100 disabled:text-stone-400">{cats.map((c) => <option key={c} value={c}>{c}</option>)}</select></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button onClick={() => setStep("map")} className="px-5 py-2.5 text-sm text-stone-600 hover:bg-stone-100 rounded-sm">Indietro</button>
            <button data-testid="commit-import-btn" onClick={commit} disabled={busy}
              className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors disabled:opacity-60">
              <CheckCircle2 size={16} /> {busy ? "Salvataggio…" : `Conferma e aggiorna (${applyCount})`}
            </button>
          </div>
        </div>
      )}

      {/* Sessions history */}
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-200 flex items-center gap-2"><History size={18} className="text-brand" /><h3 className="font-serif text-2xl text-stone-900">Sessioni di import</h3></div>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500">
            <th className="text-left font-bold py-3 px-6">File</th><th className="text-left font-bold py-3 px-6">Data</th>
            <th className="text-right font-bold py-3 px-6">Righe</th><th className="text-right font-bold py-3 px-6">Abbinati</th>
            <th className="text-right font-bold py-3 px-6">Non abb.</th><th className="text-right font-bold py-3 px-6">Aggiornati</th><th className="py-3 px-6"></th>
          </tr></thead>
          <tbody>
            {sessions.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-stone-400">Nessuna sessione salvata.</td></tr>}
            {sessions.map((s) => (
              <tr key={s.id} data-testid={`session-row-${s.id}`} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => openDetail(s.id)}>
                <td className="py-3 px-6 font-semibold text-stone-800">{s.filename}</td>
                <td className="py-3 px-6 text-stone-600">{new Date(s.imported_at).toLocaleString("it-IT")}</td>
                <td className="py-3 px-6 text-right text-stone-600">{s.total_rows}</td>
                <td className="py-3 px-6 text-right text-brand font-semibold">{s.matched_count}</td>
                <td className="py-3 px-6 text-right text-alert">{s.unmatched_count}</td>
                <td className="py-3 px-6 text-right text-stone-700">{s.updated_count}</td>
                <td className="py-3 px-6 text-right"><button data-testid={`del-session-${s.id}`} onClick={(e) => { e.stopPropagation(); setConfirmDel(s); }} className="text-stone-400 hover:text-alert"><Trash2 size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && <SessionDetail s={detail} onClose={() => setDetail(null)} />}
      {confirmDel && <ConfirmDialog open title="Eliminare la sessione?" description={`"${confirmDel.filename}" verrà rimossa dallo storico. I prezzi già aggiornati restano invariati.`} confirmLabel="Elimina" onConfirm={delSession} onClose={() => setConfirmDel(null)} />}
    </div>
  );
}

function StatCard({ label, value, tone, icon }) {
  const cls = tone === "brand" ? "text-brand" : tone === "alert" ? "text-alert" : "text-stone-900";
  return (
    <div className="bg-white border border-stone-200 rounded-sm p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-bold text-stone-500">{icon}{label}</div>
      <div className={`font-serif text-4xl font-light mt-2 ${cls}`}>{value}</div>
    </div>
  );
}

function SessionDetail({ s, onClose }) {
  const dl = async (fmt) => {
    try {
      const { data } = await api.get(`/sales/import/sessions/${s.id}/export.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = `vendite_${s.id.slice(0, 8)}.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore nel download"); }
  };
  return (
    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div data-testid="session-detail" className="bg-white rounded-sm w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-stone-200 sticky top-0 bg-white">
          <div><h3 className="font-serif text-2xl text-stone-900">{s.filename}</h3>
            <p className="text-sm text-stone-500">{new Date(s.imported_at).toLocaleString("it-IT")} · {s.total_rows} righe · {s.updated_count} aggiornati</p></div>
          <div className="flex items-center gap-2">
            <button data-testid="export-session-xlsx" onClick={() => dl("xlsx")} className="flex items-center gap-1.5 rounded-full border border-brand text-brand px-3 py-1.5 text-xs font-semibold hover:bg-brand hover:text-white transition-colors"><Download size={14} /> Excel</button>
            <button data-testid="export-session-pdf" onClick={() => dl("pdf")} className="flex items-center gap-1.5 rounded-full bg-brand text-white px-3 py-1.5 text-xs font-semibold hover:bg-brand-hover transition-colors"><Download size={14} /> PDF</button>
            <button onClick={onClose} className="text-stone-400 hover:text-stone-800"><X size={20} /></button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500 mb-2">Abbinati ({s.matched_count})</p>
            <div className="border border-stone-200 rounded-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead><tr className="bg-stone-50 text-stone-500"><th className="text-left py-2 px-3">Excel</th><th className="text-left py-2 px-3">Anagrafica</th><th className="text-right py-2 px-3">Q.tà</th><th className="text-right py-2 px-3">Prezzo</th></tr></thead>
                <tbody>{(s.matched_rows || []).map((m, i) => <tr key={i} className="border-t border-stone-100"><td className="py-1.5 px-3 text-stone-600">{m.name}</td><td className="py-1.5 px-3 text-stone-800">{m.product_name}</td><td className="py-1.5 px-3 text-right">{m.quantity ?? "—"}</td><td className="py-1.5 px-3 text-right">{m.unit_price != null ? price(m.unit_price) : "—"}</td></tr>)}</tbody>
              </table>
            </div>
          </div>
          {(s.unmatched_rows || []).length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-alert mb-2">Non abbinati ({s.unmatched_count})</p>
              <div className="border border-stone-200 rounded-sm overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-stone-50 text-stone-500"><th className="text-left py-2 px-3">Nome</th><th className="text-right py-2 px-3">Q.tà</th><th className="text-right py-2 px-3">Prezzo</th></tr></thead>
                  <tbody>{s.unmatched_rows.map((u, i) => <tr key={i} className="border-t border-stone-100"><td className="py-1.5 px-3 text-stone-700">{u.name}</td><td className="py-1.5 px-3 text-right">{u.quantity ?? "—"}</td><td className="py-1.5 px-3 text-right">{u.unit_price != null ? price(u.unit_price) : "—"}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
